import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase.js";
import { toast } from "react-toastify";
import { callableErrorMessage, formatMailboxTime } from "./mailboxUtils.js";

const PLATE_TYPE_LABELS = {
  resident: "Resident vehicle",
  owner_temp: "Guest pass (owner)",
  admin_temp: "Guest pass (admin)",
};

function GateAssignmentPanel({ group, lots, onAssign, onUnassign, busy }) {
  const [lotId, setLotId] = useState(group.gateAssignment?.lotId || "");
  const [plateType, setPlateType] = useState(group.gateAssignment?.plateType || "resident");
  const [label, setLabel] = useState(group.gateAssignment?.label || "");
  const [plateOverride, setPlateOverride] = useState(group.gateEligibility?.displayPlate || "");

  const assigned = Boolean(group.gateAssignment?.enabled);
  const eligible = group.gateEligibility?.eligible;

  if (!eligible && !assigned) {
    return (
      <div className="vehicle-gate-panel vehicle-gate-panel-muted">
        <h4>Gate auto-open</h4>
        <p>{group.gateEligibility?.reason || "Not enough vehicle detail yet."}</p>
      </div>
    );
  }

  return (
    <div className="vehicle-gate-panel">
      <h4>Gate auto-open</h4>
      {assigned ? (
        <p className="vehicle-gate-assigned">
          Assigned to <strong>{group.gateAssignment.lotTract || group.gateAssignment.lotId}</strong>
          {group.gateAssignment.displayPlate ? ` · plate ${group.gateAssignment.displayPlate}` : " · visual match at front gate"}
          {" · "}{PLATE_TYPE_LABELS[group.gateAssignment.plateType] || group.gateAssignment.plateType}
        </p>
      ) : (
        <p className="vehicle-gate-hint">{group.gateEligibility?.reason}</p>
      )}

      {!assigned ? (
        <div className="vehicle-gate-form">
          <label className="vehicle-sighting-admin-label">
            Lot
            <select
              className="vehicle-sighting-admin-select"
              value={lotId}
              onChange={(e) => setLotId(e.target.value)}
              disabled={busy}
            >
              <option value="">Select lot…</option>
              {lots.map((lot) => (
                <option key={lot.lotId} value={lot.lotId}>
                  {lot.tract}{lot.address ? ` — ${lot.address}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="vehicle-sighting-admin-label">
            Pass type
            <select
              className="vehicle-sighting-admin-select"
              value={plateType}
              onChange={(e) => setPlateType(e.target.value)}
              disabled={busy}
            >
              <option value="resident">Resident vehicle</option>
              <option value="owner_temp">Guest pass (owner)</option>
              <option value="admin_temp">Guest pass (admin)</option>
            </select>
          </label>
          {group.gateEligibility?.mode === "plate" ? (
            <label className="vehicle-sighting-admin-label">
              License plate
              <input
                className="vehicle-sighting-admin-select"
                type="text"
                value={plateOverride}
                onChange={(e) => setPlateOverride(e.target.value.toUpperCase())}
                placeholder="Plate number"
                disabled={busy}
              />
            </label>
          ) : null}
          <label className="vehicle-sighting-admin-label">
            Label (optional)
            <input
              className="vehicle-sighting-admin-select"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Mike's Subaru"
              disabled={busy}
            />
          </label>
          <button
            type="button"
            className="primary-button"
            disabled={busy || !lotId}
            onClick={() => onAssign(group.id, { lotId, plateType, label, plateOverride })}
          >
            {busy ? "Saving…" : "Enable gate access"}
          </button>
        </div>
      ) : (
        <button type="button" className="secondary-button" disabled={busy} onClick={() => onUnassign(group.id)}>
          {busy ? "Removing…" : "Remove gate access"}
        </button>
      )}
    </div>
  );
}

function SightingRow({ sighting, groupOptions, currentGroupId, onReassigned, reassigningId }) {
  const [targetGroupId, setTargetGroupId] = useState("");
  const busy = reassigningId === sighting.id;
  const mergeTargets = groupOptions.filter((g) => g.id !== currentGroupId);

  const handleReassign = async () => {
    if (!targetGroupId) {
      toast.error("Choose a vehicle to merge this sighting into");
      return;
    }
    await onReassigned(sighting.id, targetGroupId);
    setTargetGroupId("");
  };

  return (
    <article className="vehicle-sighting-row">
      {sighting.thumbnailUrl ? (
        <img
          className="vehicle-sighting-thumb"
          src={sighting.thumbnailUrl}
          alt={sighting.description || "Vehicle sighting"}
          loading="lazy"
        />
      ) : (
        <div className="vehicle-sighting-thumb vehicle-sighting-thumb-empty">No image</div>
      )}
      <div className="vehicle-sighting-body">
        <p className="vehicle-sighting-time">{formatMailboxTime(sighting.occurredAt)}</p>
        <p className="vehicle-sighting-desc">{sighting.description || "Vehicle detected"}</p>
        <p className="vehicle-sighting-meta">
          {sighting.deviceName || "Camera"}
          {sighting.adminReassigned
            ? " · Merged by admin"
            : sighting.isNewVehicle
              ? " · New vehicle (AI)"
              : " · Matched prior sighting"}
          {sighting.fingerprint?.licensePlate ? ` · plate ${sighting.fingerprint.licensePlate}` : ""}
        </p>
        {mergeTargets.length ? (
          <div className="vehicle-sighting-admin">
            <label className="vehicle-sighting-admin-label">
              Assign to existing vehicle
              <select
                className="vehicle-sighting-admin-select"
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
                disabled={busy}
                aria-label="Choose existing vehicle group"
              >
                <option value="">Select vehicle…</option>
                {mergeTargets.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label} ({g.sightingCount || 0} sightings)
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="secondary-button"
              disabled={busy || !targetGroupId}
              onClick={handleReassign}
            >
              {busy ? "Saving…" : "Merge sighting"}
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function VehicleGroupCard({
  group,
  lots,
  expanded,
  onToggle,
  groupOptions,
  onReassigned,
  onAssignGate,
  onUnassignGate,
  reassigningId,
  gateBusyId,
}) {
  const sightings = group.sightings || [];
  const isLikelyMisgrouped = sightings.length === 1 && sightings[0]?.isNewVehicle && !sightings[0]?.adminReassigned;
  const gateBusy = gateBusyId === group.id;

  return (
    <section className={`vehicle-group-card ${isLikelyMisgrouped ? "vehicle-group-card-review" : ""}`}>
      <button type="button" className="vehicle-group-header" onClick={onToggle}>
        {group.thumbnailUrl ? (
          <img className="vehicle-group-thumb" src={group.thumbnailUrl} alt={group.label} loading="lazy" />
        ) : (
          <div className="vehicle-group-thumb vehicle-sighting-thumb-empty">?</div>
        )}
        <div className="vehicle-group-summary">
          <h3>{group.label}</h3>
          <p>{group.description}</p>
          <p className="vehicle-group-stats">
            {group.sightingCount || sightings.length} sighting{(group.sightingCount || sightings.length) === 1 ? "" : "s"}
            {group.lastSeenAt ? ` · Last seen ${formatMailboxTime(group.lastSeenAt)}` : ""}
            {group.gateAssignment?.enabled ? ` · Gate: ${group.gateAssignment.lotTract || group.gateAssignment.lotId}` : ""}
            {isLikelyMisgrouped ? " · Review merge" : ""}
          </p>
        </div>
        <span className="vehicle-group-chevron">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded ? (
        <div className="vehicle-group-sightings">
          <GateAssignmentPanel
            group={group}
            lots={lots}
            onAssign={onAssignGate}
            onUnassign={onUnassignGate}
            busy={gateBusy}
          />
          {isLikelyMisgrouped ? (
            <p className="vehicle-merge-hint">
              This looks like a new vehicle group with only one sighting. If the AI split it incorrectly, merge it into an existing vehicle below.
            </p>
          ) : null}
          {sightings.length ? (
            sightings.map((sighting) => (
              <SightingRow
                key={sighting.id}
                sighting={sighting}
                groupOptions={groupOptions}
                currentGroupId={group.id}
                onReassigned={onReassigned}
                reassigningId={reassigningId}
              />
            ))
          ) : (
            <p className="vehicle-empty-sightings">No recent sightings in this period.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

function VehicleLogPage({ user, isMobile }) {
  const [groups, setGroups] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [reassigningId, setReassigningId] = useState(null);
  const [gateBusyId, setGateBusyId] = useState(null);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const groupOptions = useMemo(
    () => groups.map((g) => ({
      id: g.id,
      label: g.label,
      sightingCount: g.sightingCount || g.sightings?.length || 0,
    })),
    [groups],
  );

  const loadLog = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const getVehicleLog = httpsCallable(functions, "getVehicleLog");
      const result = await getVehicleLog({ limit: 40, days: 30 });
      const nextGroups = result.data?.groups || [];
      setGroups(nextGroups);
      setLots(result.data?.lots || []);
      setExpandedId((prev) => {
        if (prev && nextGroups.some((g) => g.id === prev)) return prev;
        return nextGroups[0]?.id || null;
      });
    } catch (err) {
      console.error("Vehicle log load error:", err);
      const message = callableErrorMessage(err);
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadLog();
  }, [user, loadLog]);

  useEffect(() => {
    if (!user) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") loadLog();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, loadLog]);

  const handleReassigned = async (sightingId, targetGroupId) => {
    setReassigningId(sightingId);
    try {
      await httpsCallable(functions, "reassignVehicleSighting")({ sightingId, targetGroupId });
      toast.success("Sighting merged into existing vehicle");
      await loadLog();
    } catch (err) {
      toast.error(callableErrorMessage(err));
    } finally {
      setReassigningId(null);
    }
  };

  const handleAssignGate = async (groupId, payload) => {
    setGateBusyId(groupId);
    try {
      await httpsCallable(functions, "assignVehicleGateAccess")(payload);
      toast.success("Vehicle linked to lot for automatic gate opening");
      await loadLog();
    } catch (err) {
      toast.error(callableErrorMessage(err));
    } finally {
      setGateBusyId(null);
    }
  };

  const handleUnassignGate = async (groupId) => {
    if (!window.confirm("Remove gate access for this vehicle?")) return;
    setGateBusyId(groupId);
    try {
      await httpsCallable(functions, "unassignVehicleGateAccess")({ groupId });
      toast.success("Gate access removed");
      await loadLog();
    } catch (err) {
      toast.error(callableErrorMessage(err));
    } finally {
      setGateBusyId(null);
    }
  };

  const handleSeedDemo = async () => {
    setSeedingDemo(true);
    try {
      await httpsCallable(functions, "seedVehicleDemo")();
      toast.success("Demo vehicles loaded — single Blue F-150 and grouped White Subaru");
      await loadLog();
    } catch (err) {
      toast.error(callableErrorMessage(err));
    } finally {
      setSeedingDemo(false);
    }
  };

  const demoButton = (
    <button
      type="button"
      className="vehicle-demo-seed-btn"
      onClick={handleSeedDemo}
      disabled={seedingDemo || loading}
    >
      {seedingDemo ? "Loading demo…" : "Load demo vehicles"}
    </button>
  );

  return (
    <div className={`page-container vehicle-log-page ${isMobile ? "page-container-mobile" : ""}`}>
      <header className="mailbox-page-header vehicle-log-page-header">
        <h1 className="page-title">Vehicle Log</h1>
        <p className="page-subtitle">
          Cars detected on selected Ring cameras. Group repeat sightings, correct AI mistakes, and assign identified vehicles to lots for automatic gate opening at the front gate camera.
        </p>
        {!loading ? <div className="vehicle-log-actions">{demoButton}</div> : null}
      </header>

      {loadError ? (
        <p className="vehicle-log-error" role="alert">
          Could not load vehicle log: {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="mailbox-loading">Loading vehicle log…</p>
      ) : groups.length ? (
        <div className="vehicle-log-list">
          {groups.map((group) => (
            <VehicleGroupCard
              key={group.id}
              group={group}
              lots={lots}
              expanded={expandedId === group.id}
              onToggle={() => setExpandedId((prev) => (prev === group.id ? null : group.id))}
              groupOptions={groupOptions}
              onReassigned={handleReassigned}
              onAssignGate={(id, payload) => handleAssignGate(id, { groupId: id, ...payload })}
              onUnassignGate={handleUnassignGate}
              reassigningId={reassigningId}
              gateBusyId={gateBusyId}
            />
          ))}
        </div>
      ) : (
        <section className="mailbox-admin-panel">
          <h2>No vehicles logged yet</h2>
          <p>
            When a selected camera detects a car, a thumbnail and AI description will appear here.
            Assign cameras under Admin → Mailbox Setup if you have not already.
          </p>
          <p>
            For testing, use <strong>Load demo vehicles</strong> above to add a single-sighting Blue F-150 (merge testing) and a grouped White Subaru with plate NCDEMO1 (gate assignment).
          </p>
        </section>
      )}
    </div>
  );
}

export default VehicleLogPage;